/**
 * Generic reader/writer for the app's cookie-backed feature flags (backoffice-managed at
 * `/backoffice/experiments`). Replaces the ad hoc regex-against-document.cookie logic that used
 * to be copy-pasted per flag (the backoffice testing page's inline `AnalyticsToggle`, and
 * `new-location-system-dev-flag.ts`'s own duplicate) with one shared implementation.
 *
 * Every flag is a plain `"true"`/`"false"` cookie, `path=/`, 1-year expiry, `SameSite=Lax`, same
 * shape a flag has had since the very first one (`dev_enable_sentry`). Enabling a flag from the
 * backoffice UI only ever sets this cookie in the admin's own browser; there is no server-side
 * per-user flag store, so a flag never affects anyone but the browser it was toggled on.
 */
const FLAG_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

/** SSR-side reader, takes a raw `Cookie` header string, since `document` doesn't exist during SSR. */
export function readFeatureFlagFromCookieHeader(
  cookieHeader: string | null | undefined,
  cookieName: string,
  defaultValue: boolean
): boolean {
  if (!cookieHeader) return defaultValue;
  const match = cookieHeader.match(new RegExp(`(?:^|; )${cookieName}=([^;]+)`));
  return match ? match[1] === 'true' : defaultValue;
}

/** Client-side reader, via `document.cookie`. */
export function readFeatureFlagFromDocumentCookie(cookieName: string, defaultValue: boolean): boolean {
  if (typeof document === 'undefined') return defaultValue;
  return readFeatureFlagFromCookieHeader(document.cookie, cookieName, defaultValue);
}

/** Client-side writer, sets/overwrites the flag cookie in the current browser only. */
export function writeFeatureFlagCookie(cookieName: string, value: boolean): void {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + FLAG_COOKIE_MAX_AGE_SECONDS * 1000).toUTCString();
  document.cookie = `${cookieName}=${value}; path=/; expires=${expires}; SameSite=Lax`;
}
